import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EmptyState } from '../../../components/app';
import { useTokenColors, type TokenName } from '../../../hooks/useTokenColors';
import { CashflowChartsProps } from '../types';

/** Линии по провайдерам — цвета токенов; для незнакомого кода — text-3. */
const PROVIDER_TOKENS: Record<string, TokenName> = {
  click: 'text',
  payme: 'accent',
  paylov: 'pos',
  apple: 'warn',
};

const compact = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн`;
  if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000)} тыс.`;
  return String(value);
};

export function CashflowCharts({
  providers,
  daily,
  formatMoney,
}: CashflowChartsProps) {
  const c = useTokenColors();
  const gridColor = c['border-soft'];
  const axisColor = c['text-2'];

  // Провайдеры считаются в разных валютах, поэтому на графике — сумовые
  // эквиваленты: иначе доллары Apple встали бы рядом с сумами как равные.
  const byProvider = useMemo(
    () =>
      providers.map((p) => ({
        name: p.name,
        Чистое: p.netUzs,
        Поступило: p.settledUzs,
        Осталось: p.outstandingUzs,
      })),
    [providers],
  );

  // Точки графика: одна строка на дату, колонка на каждого провайдера.
  const timeline = useMemo(() => {
    const byDate = new Map<string, Record<string, number | string>>();
    daily.forEach((point) => {
      const row = byDate.get(point.date) || { date: point.date };
      row[point.code] = ((row[point.code] as number) || 0) + point.grossUzs;
      byDate.set(point.date, row);
    });
    return Array.from(byDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [daily]);

  // Внутри вкладки своя тень не нужна — иначе карточка в карточке.
  const cardCls = 'rounded-card border border-border-soft p-[18px]';
  const titleCls = 'mb-4 font-display text-[17px] font-bold';

  const tooltipStyle = {
    backgroundColor: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: '12px',
    color: c.text,
  };
  const tick = { fill: axisColor, fontSize: 12 };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className={cardCls}>
        <h3 className={titleCls}>По провайдерам</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={byProvider}>
            <CartesianGrid stroke={gridColor} vertical={false} />
            <XAxis dataKey="name" tick={tick} stroke={axisColor} tickLine={false} axisLine={false} />
            <YAxis stroke={axisColor} tickFormatter={compact} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: c.muted }}
              contentStyle={tooltipStyle}
              formatter={(value) => formatMoney(Number(value) || 0)}
            />
            <Legend wrapperStyle={{ color: axisColor, fontSize: 13 }} />
            <Bar dataKey="Чистое" fill={c['text-3']} radius={[6, 6, 0, 0]} />
            <Bar dataKey="Поступило" fill={c.text} radius={[6, 6, 0, 0]} />
            <Bar dataKey="Осталось" fill={c.accent} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={cardCls}>
        <h3 className={titleCls}>Собрано по дням</h3>
        {timeline.length === 0 ? (
          <EmptyState className="h-[300px]" title="За выбранный период платежей нет" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline}>
              <CartesianGrid stroke={gridColor} vertical={false} />
              <XAxis dataKey="date" tick={tick} stroke={axisColor} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis stroke={axisColor} tickFormatter={compact} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => formatMoney(Number(value) || 0)}
              />
              <Legend wrapperStyle={{ color: axisColor, fontSize: 13 }} />
              {providers.map((provider) => (
                <Line
                  key={provider.code}
                  type="monotone"
                  dataKey={provider.code}
                  name={provider.name}
                  stroke={c[PROVIDER_TOKENS[provider.code] ?? 'text-3']}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
